/**
 * Avatar unlock helpers — derive which items are accessible at a given
 * XP level + badge collection, and validate equip requests.
 *
 * Two-gate unlock model:
 *   1. `unlockXp`: minimum total XP the child must have accumulated.
 *   2. `requiredBadges` (optional): an array of badge ids ALL of which
 *      must be earned. Lets a hat be gated behind exercise-type diversity,
 *      streak achievements, training-day counts, etc. by reusing the badge
 *      condition engine.
 *
 * Callers may pass `earnedBadges` either as an array of ids or a Set.
 * A missing/empty value is treated as "no badges earned yet".
 */

import { AVATAR_CATALOG, AVATAR_SLOTS, DEFAULT_AVATAR } from '../data/avatarCatalog.jsx';

function toBadgeSet(earnedBadges) {
  if (earnedBadges instanceof Set) return earnedBadges;
  if (Array.isArray(earnedBadges)) return new Set(earnedBadges);
  return new Set();
}

function meetsBadgeGate(item, badgeSet) {
  if (!Array.isArray(item.requiredBadges) || item.requiredBadges.length === 0) {
    return true;
  }
  for (const id of item.requiredBadges) {
    if (!badgeSet.has(id)) return false;
  }
  return true;
}

/**
 * Return only the items the child has unlocked at the given XP & badge
 * state, grouped by slot.
 *
 * Shape: { hat: [...items], glasses: [...] }
 */
export function unlockedItems(xp = 0, earnedBadges = null) {
  const safeXp = typeof xp === 'number' ? xp : 0;
  const badgeSet = toBadgeSet(earnedBadges);
  const out = {};
  for (const slot of AVATAR_SLOTS) {
    out[slot] = (AVATAR_CATALOG[slot] ?? []).filter(
      (it) => (it.unlockXp ?? 0) <= safeXp && meetsBadgeGate(it, badgeSet)
    );
  }
  return out;
}

/**
 * True if the given item id in the given slot is unlocked at this XP and
 * badge state. Unknown items return false (defensive).
 */
export function isUnlocked(slot, itemId, xp = 0, earnedBadges = null) {
  const item = (AVATAR_CATALOG[slot] ?? []).find((it) => it.id === itemId);
  if (!item) return false;
  const safeXp = typeof xp === 'number' ? xp : 0;
  if ((item.unlockXp ?? 0) > safeXp) return false;
  return meetsBadgeGate(item, toBadgeSet(earnedBadges));
}

/**
 * Resolve an `equipped` map into an array of `{slot, item}` for rendering.
 * Items with `render === null` (i.e. `none`) are skipped.
 */
export function resolveEquipped(equipped) {
  const safe = equipped ?? DEFAULT_AVATAR;
  const out = [];
  for (const slot of AVATAR_SLOTS) {
    const id = safe[slot] ?? DEFAULT_AVATAR[slot];
    const item = (AVATAR_CATALOG[slot] ?? []).find((it) => it.id === id);
    if (item && item.render) out.push({ slot, item });
  }
  return out;
}

/**
 * Sanitize a stored `equipped` map so it never references an item the
 * child no longer has access to (e.g. progress reset) or an unknown id
 * from a future build. Accepts the same `earnedBadges` shape as
 * `isUnlocked`.
 */
export function sanitizeEquipped(equipped, xp = 0, earnedBadges = null) {
  const safe = { ...DEFAULT_AVATAR };
  for (const slot of AVATAR_SLOTS) {
    const id = equipped?.[slot];
    if (id && isUnlocked(slot, id, xp, earnedBadges)) {
      safe[slot] = id;
    }
  }
  return safe;
}
